import { createContext, useContext, useEffect, useState } from 'react'
import { authService, type AuthUser, type FederatedProvider } from '@/lib/auth'

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  authService: typeof authService
  signIn: (username: string, password: string) => Promise<void>
  signUp: (
    username: string,
    password: string,
    email: string,
    firstName: string,
    lastName: string,
  ) => Promise<void>
  signOut: () => Promise<void>
  confirmSignUp: (username: string, code: string) => Promise<void>
  forgotPassword: (username: string) => Promise<void>
  resetPassword: (
    username: string,
    code: string,
    newPassword: string,
  ) => Promise<void>
  federatedSignIn: (provider: FederatedProvider) => Promise<void>
  registerPostLoginAction: (action: () => void | Promise<void>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [postLoginActions, setPostLoginActions] = useState<Array<() => void | Promise<void>>>([])

  const registerPostLoginAction = (action: () => void | Promise<void>) => {
    setPostLoginActions(prev => [...prev, action])
  }

  const executePostLoginActions = async () => {
    for (const action of postLoginActions) {
      await action()
    }
    setPostLoginActions([])
  }

  useEffect(() => {
    checkAuthState()
  }, [])

  async function checkAuthState() {
    try {
      setIsLoading(true)
      const isAuthenticated = await authService.isAuthenticated()

      if (isAuthenticated) {
        // Add a small delay to ensure tokens are available
        await new Promise(resolve => setTimeout(resolve, 500))
        const currentUser = await authService.getCurrentUser()
        setUser(currentUser)
      } else {
        setUser(null)
      }
    } catch (error) {
      console.warn('Error checking auth state:', error)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignIn = async (username: string, password: string) => {
    const result = await authService.signIn(username, password)
    setUser(result.user)
    // Execute any pending post-login actions
    await executePostLoginActions()
  }

  const handleSignUp = async (
    username: string,
    password: string,
    email: string,
    firstName: string,
    lastName: string,
  ) => {
    await authService.signUp(username, password, email, firstName, lastName)
  }

  const handleConfirmSignUp = async (username: string, code: string) => {
    await authService.confirmSignUp(username, code)
  }

  const handleSignOut = async () => {
    await authService.signOut()
    setUser(null)
  }

  const handleResetPassword = async (username: string) => {
    await authService.resetPassword(username)
  }

  const handleConfirmResetPassword = async (
    username: string,
    code: string,
    newPassword: string,
  ) => {
    await authService.confirmResetPassword(username, code, newPassword)
  }

  const handleFederatedSignIn = async (provider: FederatedProvider) => {
    // Store current location for redirect after sign-in
    localStorage.setItem(
      'auth_redirect_after_signin',
      window.location.pathname === '/auth/signin'
        ? '/'
        : window.location.pathname,
    )
    await authService.federatedSignIn(provider)
  }

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    authService,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    confirmSignUp: handleConfirmSignUp,
    forgotPassword: handleResetPassword,
    resetPassword: handleConfirmResetPassword,
    federatedSignIn: handleFederatedSignIn,
    registerPostLoginAction,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
